import React from 'react';
import { NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppTopBar } from '../components/AppTopBar';
import { AppSelect } from '../components/AppSelect';
import { AppSidebar } from '../components/AppSidebar';
import {
  SETTINGS_PATH_ACTIVITY_TYPES,
  SETTINGS_PATH_CHECKLISTS,
  SETTINGS_PATH_PLANTAFEL,
  SETTINGS_PATH_BUSINESS,
  SETTINGS_PATH_DOCUMENT_NUMBERING,
  SETTINGS_PATH_CUSTOMER_NUMBERING,
  SETTINGS_PATH_DOCUMENT_TEXTS,
  SETTINGS_PATH_INVOICE_TEMPLATE,
  SETTINGS_PATH_EMAIL_DISPATCH,
  SETTINGS_PATH_LEAD_PIPELINE,
  SETTINGS_PATH_LEAD_CATEGORIES,
  SETTINGS_PATH_PERSONAL,
  SETTINGS_PATH_NOTIFICATIONS,
  SETTINGS_PATH_OFFERS_INVOICES,
  SETTINGS_PATH_PAYMENT_CONDITIONS,
  SETTINGS_PATH_LABOR_COST_SHARE,
  SETTINGS_PATH_LEGAL_ATTACHMENTS,
  SETTINGS_PATH_PAYROLL_ADJUSTMENT_LOHNARTEN,
  SETTINGS_PATH_EMPLOYEE_DOCUMENT_TYPES,
  SETTINGS_PATH_ROLES,
  SETTINGS_PATH_INVOICE_FORWARDING,
  SETTINGS_PATH_USERS,
  SETTINGS_PATH_USERS_API,
  SETTINGS_PATH_INTEGRATIONS,
  SETTINGS_PATH_DATEV,
  SETTINGS_PATH_INTEGRATION_IDS,
  SETTINGS_PATH_INTEGRATION_METAL,
  SETTINGS_PATH_INTEGRATION_DKV,
  SETTINGS_PATH_INTEGRATION_MEINSHK,
  SETTINGS_PATH_INTEGRATION_OMD,
  SETTINGS_PATH_OMD,
  SETTINGS_PATH_OMD_GC,
  SETTINGS_PATH_IDS,
  SETTINGS_PATH_RECYCLE_BIN,
  SETTINGS_PATH_ASSISTANT,
  isSettingsSubPath,
  roleIdFromSettingsPath,
} from '../utils/settingsRoutes';
import { ActivityTypesSettingsSection } from '../features/settings/sections/ActivityTypesSettingsSection';
import { CompanySettingsSection } from '../features/settings/sections/CompanySettingsSection';
import { DocumentNumberingSettingsSection } from '../features/settings/sections/DocumentNumberingSettingsSection';
import { CustomerNumberingSettingsSection } from '../features/settings/sections/CustomerNumberingSettingsSection';
import { ProjectNumberingSettingsSection } from '../features/settings/sections/ProjectNumberingSettingsSection';
import { DocumentTextsSettingsSection } from '../features/settings/sections/DocumentTextsSettingsSection';
import { InvoiceTemplateSettingsSection } from '../features/settings/sections/InvoiceTemplateSettingsSection';
import { EmailSettingsSection } from '../features/settings/sections/EmailSettingsSection';
import { InvoiceForwardingSettingsSection } from '../features/settings/sections/InvoiceForwardingSettingsSection';
import { LeadCategoriesSettingsSection } from '../features/settings/sections/LeadCategoriesSettingsSection';
import { LeadPipelineSettingsSection } from '../features/settings/sections/LeadPipelineSettingsSection';
import type { LeadCategoryRow } from '../features/settings/types';
import { DatevSettingsPage } from '../features/settings/sections/datev/DatevSettingsPage';
import { IntegrationsSettingsSection } from '../features/settings/sections/IntegrationsSettingsSection';
import { IntegrationDetailPage } from '../features/settings/sections/integrations/IntegrationDetailPage';
import type { LeadPipeline } from '../types';
import {
  OffersInvoicesSettingsSection,
  offersInvoicesSettingsLabel,
} from '../features/settings/sections/OffersInvoicesSettingsSection';
import { PayrollAdjustmentLohnartenSettingsSection } from '../features/settings/sections/PayrollAdjustmentLohnartenSettingsSection';
import { EmployeeDocumentTypesSettingsSection } from '../features/settings/sections/EmployeeDocumentTypesSettingsSection';
import { NotificationSettingsSection } from '../features/settings/sections/NotificationSettingsSection';
import { PersonalSettingsSection } from '../features/settings/sections/PersonalSettingsSection';
import { PlantafelSettingsSection } from '../features/settings/sections/PlantafelSettingsSection';
import { ChecklistsSettingsSection } from '../features/settings/sections/ChecklistsSettingsSection';
import { RolesRightsSettingsSection } from '../features/settings/sections/RolesRightsSettingsSection';
import { UsersSettingsSection } from '../features/settings/sections/UsersSettingsSection';
import { RecycleBinSettingsSection } from '../features/settings/sections/RecycleBinSettingsSection';
import { fetchMe, getCachedCapabilities } from '../lib/meClient';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { INSTANCE_MODULES, isModuleEnabled, isModuleSettingsPath, isSettingsPathAvailable } from '../lib/instanceModules';

/** Mobile Auswahl: Tab → Pfad (auch zum Ausblenden abgewählter Module). */
const MOBILE_TAB_PATHS: Record<string, string> = {
  unternehmen: SETTINGS_PATH_BUSINESS,
  users: SETTINGS_PATH_USERS,
  recycle_bin: SETTINGS_PATH_RECYCLE_BIN,
  roles: SETTINGS_PATH_ROLES,
  users_api: SETTINGS_PATH_USERS_API,
  integrations: SETTINGS_PATH_INTEGRATIONS,
  document_texts: SETTINGS_PATH_DOCUMENT_TEXTS,
  invoice_template: SETTINGS_PATH_INVOICE_TEMPLATE,
  lead_pipeline: SETTINGS_PATH_LEAD_PIPELINE,
  offers_invoices: SETTINGS_PATH_OFFERS_INVOICES,
  invoice_forwarding: SETTINGS_PATH_INVOICE_FORWARDING,
  document_numbering: SETTINGS_PATH_DOCUMENT_NUMBERING,
  customer_numbering: SETTINGS_PATH_CUSTOMER_NUMBERING,
  payroll_adjustment_lohnarten: SETTINGS_PATH_PAYROLL_ADJUSTMENT_LOHNARTEN,
  employee_document_types: SETTINGS_PATH_EMPLOYEE_DOCUMENT_TYPES,
  aktivitaeten: SETTINGS_PATH_ACTIVITY_TYPES,
  checklisten: SETTINGS_PATH_CHECKLISTS,
  plantafel: SETTINGS_PATH_PLANTAFEL,
  personal: SETTINGS_PATH_PERSONAL,
  notifications: SETTINGS_PATH_NOTIFICATIONS,
  email: SETTINGS_PATH_EMAIL_DISPATCH,
};

type SettingsNavTab =
  | 'unternehmen'
  | 'users'
  | 'roles'
  | 'users_api'
  | 'integrations'
  | 'personal'
  | 'notifications'
  | 'email'
  | 'document_texts'
  | 'invoice_template'
  | 'lead_pipeline'
  | 'aktivitaeten'
  | 'checklisten'
  | 'plantafel'
  | 'offers_invoices'
  | 'payroll_adjustment_lohnarten'
  | 'employee_document_types'
  | 'invoice_forwarding'
  | 'document_numbering'
  | 'customer_numbering'
  | 'recycle_bin';

export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [capabilities, setCapabilities] = React.useState<Record<string, boolean> | null>(() => getCachedCapabilities());
  const [capabilitiesLoaded, setCapabilitiesLoaded] = React.useState(() => getCachedCapabilities() !== null);
  const { loaded: modulesLoaded, modules } = useModuleAccess();
  // Settings-Seiten abgewählter Module (Admin Center) verschwinden aus Navigation und Auswahl.
  // Bis die Module bekannt sind, bleiben modulgebundene Einträge weg (kein Aufblitzen).
  const showPath = React.useCallback(
    (path: string) => (modulesLoaded ? isSettingsPathAvailable(path, modules) : !isModuleSettingsPath(path)),
    [modulesLoaded, modules],
  );
  // Das Layout gilt für alle Belege; ohne Rechnungen heißt es nicht „Rechnungsvorlage“.
  const layoutTemplateLabel = modulesLoaded && !isModuleEnabled(modules, 'rechnungen') ? 'Dokumentvorlage' : 'Rechnungsvorlage';
  const showIntegrations =
    modulesLoaded &&
    INSTANCE_MODULES.some((module) => module.group === 'integration' && isModuleEnabled(modules, module.key));
  // „Angebote & Rechnungen“ heißt je nach Modulen nur „Angebote“ oder „Rechnungen“; null = ausblenden.
  const offersInvoicesLabel = modulesLoaded ? offersInvoicesSettingsLabel(modules) : null;
  const [leadPipelines, setLeadPipelines] = React.useState<LeadPipeline[]>([]);
  const [leadCategories, setLeadCategories] = React.useState<LeadCategoryRow[]>([]);
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [location.pathname]);
  const plainPath = React.useMemo(() => location.pathname.replace(/^\/i\/[^/]+/, ''), [location.pathname]);
  const withInstancePath = React.useCallback(
    (path: string) => `${instancePrefix}${path.startsWith('/') ? path : `/${path}`}`,
    [instancePrefix]
  );

  React.useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setCapabilities((me?.capabilities as Record<string, boolean> | undefined) ?? {});
        setCapabilitiesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCapabilities((prev) => prev ?? {});
        setCapabilitiesLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setCapabilitiesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canViewOrgSettings = Boolean(
    capabilities && (capabilities['web.einstellungen.view'] === true),
  );
  const settingsTab: SettingsNavTab = React.useMemo(() => {
    if (plainPath === SETTINGS_PATH_PERSONAL) return 'personal';
    if (plainPath === SETTINGS_PATH_NOTIFICATIONS) return 'notifications';
    if (plainPath === SETTINGS_PATH_EMAIL_DISPATCH) return 'email';
    if (plainPath === SETTINGS_PATH_DOCUMENT_TEXTS) return 'document_texts';
    if (plainPath === SETTINGS_PATH_INVOICE_TEMPLATE) return 'invoice_template';
    if (plainPath === SETTINGS_PATH_LEAD_PIPELINE) return 'lead_pipeline';
    if (plainPath === SETTINGS_PATH_ACTIVITY_TYPES) return 'aktivitaeten';
    if (
      plainPath === SETTINGS_PATH_CHECKLISTS ||
      plainPath.startsWith(`${SETTINGS_PATH_CHECKLISTS}/`)
    ) return 'checklisten';
    if (plainPath === SETTINGS_PATH_PLANTAFEL) return 'plantafel';
    if (plainPath === SETTINGS_PATH_OFFERS_INVOICES) return 'offers_invoices';
    if (plainPath === SETTINGS_PATH_PAYROLL_ADJUSTMENT_LOHNARTEN) return 'payroll_adjustment_lohnarten';
    if (plainPath === SETTINGS_PATH_EMPLOYEE_DOCUMENT_TYPES) return 'employee_document_types';
    if (plainPath === SETTINGS_PATH_INVOICE_FORWARDING) return 'invoice_forwarding';
    if (plainPath === SETTINGS_PATH_DOCUMENT_NUMBERING) return 'document_numbering';
    if (plainPath === SETTINGS_PATH_CUSTOMER_NUMBERING) return 'customer_numbering';
    if (plainPath === SETTINGS_PATH_ROLES || roleIdFromSettingsPath(plainPath)) return 'roles';
    if (plainPath === SETTINGS_PATH_USERS_API) return 'users_api';
    if (
      plainPath === SETTINGS_PATH_INTEGRATIONS ||
      plainPath.startsWith(`${SETTINGS_PATH_INTEGRATIONS}/`) ||
      plainPath === SETTINGS_PATH_OMD ||
      plainPath === SETTINGS_PATH_OMD_GC ||
      plainPath === SETTINGS_PATH_IDS
    ) return 'integrations';
    if (plainPath === SETTINGS_PATH_USERS) return 'users';
    if (plainPath === SETTINGS_PATH_RECYCLE_BIN) return 'recycle_bin';
    return 'unternehmen';
  }, [plainPath]);

  const integrationDetailId =
    plainPath === SETTINGS_PATH_INTEGRATION_OMD
      ? 'omd'
      : plainPath === SETTINGS_PATH_INTEGRATION_IDS
        ? 'ids'
        : plainPath === SETTINGS_PATH_INTEGRATION_METAL
          ? 'metal'
          : plainPath === SETTINGS_PATH_INTEGRATION_DKV
            ? 'dkv'
            : plainPath === SETTINGS_PATH_INTEGRATION_MEINSHK
              ? 'meinshk'
              : null;

  const settingsNavBtn = (active: boolean) =>
    `w-full flex items-center px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
      active
        ? 'bg-app-surface-muted text-app-text font-medium'
        : 'text-app-text-muted font-normal hover:bg-app-hover'
    }`;

  const settingsNavGroup = 'mt-6 first:mt-1';
  const settingsNavGroupLabel =
    'px-3 pb-1.5 text-xs font-semibold text-app-text';

  // Skills und geplante Läufe stehen im KI-Chat unter „Anpassungen“ bzw. „Geplant“.
  if (plainPath === SETTINGS_PATH_ASSISTANT) {
    return <Navigate to={withInstancePath('/chat?view=customize')} replace />;
  }
  if (plainPath === SETTINGS_PATH_LEAD_CATEGORIES) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_LEAD_PIPELINE)} replace />;
  }
  if (plainPath === SETTINGS_PATH_OMD || plainPath === SETTINGS_PATH_OMD_GC) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_INTEGRATION_OMD)} replace />;
  }
  if (plainPath === SETTINGS_PATH_IDS) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_INTEGRATION_IDS)} replace />;
  }
  if (
    plainPath === SETTINGS_PATH_PAYMENT_CONDITIONS ||
    plainPath === SETTINGS_PATH_LABOR_COST_SHARE ||
    plainPath === SETTINGS_PATH_LEGAL_ATTACHMENTS
  ) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_OFFERS_INVOICES)} replace />;
  }

  if (!isSettingsSubPath(plainPath)) {
    if (!capabilitiesLoaded) {
      return (
        <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
          <AppSidebar active="settings" />
          <main className="flex-1 min-h-0" />
        </div>
      );
    }
    return (
      <Navigate
        to={withInstancePath(canViewOrgSettings ? SETTINGS_PATH_BUSINESS : SETTINGS_PATH_PERSONAL)}
        replace
      />
    );
  }

  if (
    capabilitiesLoaded &&
    !canViewOrgSettings &&
    settingsTab !== 'personal' &&
    settingsTab !== 'notifications'
  ) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_PERSONAL)} replace />;
  }
  if (
    modulesLoaded &&
    (!showPath(plainPath) ||
      (settingsTab === 'integrations' && !showIntegrations) ||
      (settingsTab === 'offers_invoices' && !offersInvoicesLabel))
  ) {
    return <Navigate to={withInstancePath(SETTINGS_PATH_BUSINESS)} replace />;
  }
  if (
    !modulesLoaded &&
    (isModuleSettingsPath(plainPath) || settingsTab === 'integrations' || settingsTab === 'offers_invoices')
  ) {
    // Modulgebundene Seite: erst rendern, wenn klar ist, dass das Modul an ist (sonst 403-Anfragen).
    return (
      <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
        <AppSidebar active="settings" />
        <main className="flex-1 min-h-0" />
      </div>
    );
  }

  const showOrgSettings = canViewOrgSettings;

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-bg font-sans">
      <AppSidebar active="settings" />

      <aside className="hidden md:flex w-[272px] shrink-0 border-r border-app-border bg-app-surface flex-col min-h-0 h-full overflow-hidden self-stretch">
        <div className="px-3 pt-5 pb-3 shrink-0">
          <h1 className="text-lg font-bold text-app-text tracking-tight">Einstellungen</h1>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain app-scrollbar px-3 pt-2 pb-6">
          {showOrgSettings ? (
            <>
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Unternehmen</p>
            <ul>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_BUSINESS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Unternehmensangaben
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_USERS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Nutzer
                </NavLink>
              </li>
              <li>
                <NavLink to={withInstancePath(SETTINGS_PATH_ROLES)} className={({ isActive }) => settingsNavBtn(isActive || Boolean(roleIdFromSettingsPath(plainPath)))}>
                  Rollen & Rechte
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_RECYCLE_BIN)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Papierkorb
                </NavLink>
              </li>
            </ul>
          </div>
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Dokumente &amp; Versand</p>
            <ul>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_DOCUMENT_TEXTS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Textvorlagen
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_INVOICE_TEMPLATE)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  {layoutTemplateLabel}
                </NavLink>
              </li>
              {showPath(SETTINGS_PATH_EMAIL_DISPATCH) ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_EMAIL_DISPATCH)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    E-Mail Versand
                  </NavLink>
                </li>
              ) : null}
            </ul>
          </div>
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Buchhaltung &amp; Rechnungswesen</p>
            <ul>
              {offersInvoicesLabel ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_OFFERS_INVOICES)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    {offersInvoicesLabel}
                  </NavLink>
                </li>
              ) : null}
              {showPath(SETTINGS_PATH_INVOICE_FORWARDING) ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_INVOICE_FORWARDING)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    Rechnungsweiterleitung
                  </NavLink>
                </li>
              ) : null}
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_DOCUMENT_NUMBERING)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Nummernkreise
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_CUSTOMER_NUMBERING)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Kunden- &amp; Projektnummern
                </NavLink>
              </li>
            </ul>
          </div>
          {showPath(SETTINGS_PATH_PAYROLL_ADJUSTMENT_LOHNARTEN) ? (
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Lohnbuchhaltung</p>
            <ul>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_PAYROLL_ADJUSTMENT_LOHNARTEN)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Be- und Abzüge
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_EMPLOYEE_DOCUMENT_TYPES)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Mitarbeiter-Dokumente
                </NavLink>
              </li>
            </ul>
          </div>
          ) : null}
          {[SETTINGS_PATH_LEAD_PIPELINE, SETTINGS_PATH_ACTIVITY_TYPES, SETTINGS_PATH_PLANTAFEL, SETTINGS_PATH_CHECKLISTS].some(showPath) ? (
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Projekte &amp; Aktivitäten</p>
            <ul>
              {showPath(SETTINGS_PATH_LEAD_PIPELINE) ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_LEAD_PIPELINE)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    Projekt-Pipeline
                  </NavLink>
                </li>
              ) : null}
              {showPath(SETTINGS_PATH_ACTIVITY_TYPES) ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_ACTIVITY_TYPES)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    Aktivitätstypen
                  </NavLink>
                </li>
              ) : null}
              {showPath(SETTINGS_PATH_PLANTAFEL) ? (
                <li>
                  <NavLink end to={withInstancePath(SETTINGS_PATH_PLANTAFEL)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    Plantafel
                  </NavLink>
                </li>
              ) : null}
              {showPath(SETTINGS_PATH_CHECKLISTS) ? (
                <li>
                  <NavLink to={withInstancePath(SETTINGS_PATH_CHECKLISTS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                    Checklisten
                  </NavLink>
                </li>
              ) : null}
            </ul>
          </div>
          ) : null}
          {showIntegrations ? (
          <div className={settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Integrationen</p>
            <ul>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_INTEGRATIONS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Integrationen
                </NavLink>
              </li>
            </ul>
          </div>
          ) : null}
          <div className="mt-6 border-t border-app-border" />
            </>
          ) : null}
          <div className={showOrgSettings ? 'mt-3' : settingsNavGroup}>
            <p className={settingsNavGroupLabel}>Persönliche Einstellungen</p>
            <ul>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_PERSONAL)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Profil
                </NavLink>
              </li>
              <li>
                <NavLink end to={withInstancePath(SETTINGS_PATH_NOTIFICATIONS)} className={({ isActive }) => settingsNavBtn(isActive)}>
                  Benachrichtigungen
                </NavLink>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      <main className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar />

        <div className="md:hidden shrink-0 border-b border-app-border bg-app-surface px-3 py-2">
          <AppSelect
            value={settingsTab === 'users_api' ? 'integrations' : settingsTab}
            onChange={(v) => {
              if (MOBILE_TAB_PATHS[v]) navigate(withInstancePath(MOBILE_TAB_PATHS[v]));
            }}
            options={(
              showOrgSettings
                ? [
                    { value: 'unternehmen', label: 'Unternehmensangaben' },
                    { value: 'users', label: 'Nutzer' },
                    { value: 'roles', label: 'Rollen & Rechte' },
                    { value: 'recycle_bin', label: 'Papierkorb' },
                    { value: 'document_texts', label: 'Textvorlagen' },
                    { value: 'invoice_template', label: layoutTemplateLabel },
                    { value: 'email', label: 'E-Mail Versand' },
                    { value: 'offers_invoices', label: offersInvoicesLabel ?? 'Angebote & Rechnungen' },
                    { value: 'invoice_forwarding', label: 'Rechnungsweiterleitung' },
                    { value: 'document_numbering', label: 'Nummernkreise' },
                    { value: 'customer_numbering', label: 'Kunden- & Projektnummern' },
                    { value: 'payroll_adjustment_lohnarten', label: 'Lohnbuchhaltung · Be- und Abzüge' },
                    { value: 'employee_document_types', label: 'Lohnbuchhaltung · Mitarbeiter-Dokumente' },
                    { value: 'lead_pipeline', label: 'Projekt-Pipeline' },
                    { value: 'aktivitaeten', label: 'Aktivitätstypen' },
                    { value: 'plantafel', label: 'Plantafel' },
                    { value: 'checklisten', label: 'Checklisten' },
                    { value: 'integrations', label: 'Integrationen' },
                    { value: 'personal', label: 'Profil' },
                    { value: 'notifications', label: 'Benachrichtigungen' },
                  ]
                : [
                    { value: 'personal', label: 'Profil' },
                    { value: 'notifications', label: 'Benachrichtigungen' },
                  ]
            ).filter(
              (option) =>
                (option.value !== 'integrations' || showIntegrations) &&
                (option.value !== 'offers_invoices' || offersInvoicesLabel !== null) &&
                showPath(MOBILE_TAB_PATHS[option.value] ?? ''),
            )}
            className="w-full"
          />
        </div>

        <div
          className={`flex-1 min-h-0 overflow-y-auto app-scrollbar bg-app-surface-muted/50 ${
            settingsTab === 'roles' ? 'p-0' : 'px-3 py-3 md:px-6 md:py-6'
          }`}
        >
          <div className={`w-full min-w-0 max-w-full ${settingsTab === 'roles' ? 'h-full' : ''}`}>
            {showOrgSettings && settingsTab === 'unternehmen' && <CompanySettingsSection />}
            {showOrgSettings && settingsTab === 'users' && <UsersSettingsSection />}
            {showOrgSettings && settingsTab === 'recycle_bin' && <RecycleBinSettingsSection />}
            {showOrgSettings && settingsTab === 'roles' && <RolesRightsSettingsSection />}
            {showOrgSettings && settingsTab === 'users_api' && (
              <IntegrationDetailPage id="lead_api" onBack={() => navigate(withInstancePath(SETTINGS_PATH_INTEGRATIONS))} />
            )}
            {showOrgSettings && settingsTab === 'integrations' && plainPath === SETTINGS_PATH_DATEV && (
              <DatevSettingsPage onBack={() => navigate(withInstancePath(SETTINGS_PATH_INTEGRATIONS))} />
            )}
            {showOrgSettings && settingsTab === 'integrations' && integrationDetailId && (
              <IntegrationDetailPage
                key={integrationDetailId}
                id={integrationDetailId}
                onBack={() => navigate(withInstancePath(SETTINGS_PATH_INTEGRATIONS))}
              />
            )}
            {showOrgSettings && settingsTab === 'integrations' && plainPath === SETTINGS_PATH_INTEGRATIONS && (
              <IntegrationsSettingsSection onOpen={(path) => navigate(withInstancePath(path))} />
            )}
            {showOrgSettings && settingsTab === 'plantafel' && <PlantafelSettingsSection />}
            {settingsTab === 'personal' && <PersonalSettingsSection />}
            {settingsTab === 'notifications' && <NotificationSettingsSection />}
            {showOrgSettings && settingsTab === 'email' && <EmailSettingsSection />}
            {showOrgSettings && settingsTab === 'document_texts' && <DocumentTextsSettingsSection />}
            {showOrgSettings && settingsTab === 'invoice_template' && <InvoiceTemplateSettingsSection />}
            {showOrgSettings && settingsTab === 'lead_pipeline' && (
              <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
                <LeadPipelineSettingsSection
                  onPipelinesChange={setLeadPipelines}
                  categories={leadCategories}
                />
                <LeadCategoriesSettingsSection
                  pipelines={leadPipelines}
                  onCategoriesChange={setLeadCategories}
                />
              </div>
            )}
            {showOrgSettings && settingsTab === 'aktivitaeten' && <ActivityTypesSettingsSection />}
            {showOrgSettings && settingsTab === 'checklisten' && <ChecklistsSettingsSection />}
            {showOrgSettings && settingsTab === 'offers_invoices' && <OffersInvoicesSettingsSection />}
            {showOrgSettings && settingsTab === 'payroll_adjustment_lohnarten' && <PayrollAdjustmentLohnartenSettingsSection />}
            {showOrgSettings && settingsTab === 'employee_document_types' && <EmployeeDocumentTypesSettingsSection />}
            {showOrgSettings && settingsTab === 'invoice_forwarding' && <InvoiceForwardingSettingsSection />}
            {showOrgSettings && settingsTab === 'document_numbering' && <DocumentNumberingSettingsSection />}
            {showOrgSettings && settingsTab === 'customer_numbering' && (
              <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5 items-start">
                <CustomerNumberingSettingsSection />
                <ProjectNumberingSettingsSection />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
